import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, MONTHS } from "@/lib/api";
import { useLiveReload } from "@/lib/live";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import MonthGrid, { ymd } from "@/components/MonthGrid";
import AutofillButton from "@/components/AutofillButton";
import PostItAgenda from "@/components/PostItAgenda";
import PostDetailDialog from "@/components/PostDetailDialog";
import PostEditorDialog from "@/components/PostEditorDialog";

export default function CalendarPage() {
  const { clientId, client } = useClient();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [posts, setPosts] = useState([]);
  const [files, setFiles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editor, setEditor] = useState(null);

  const load = useCallback(() => {
    api.get("/posts", { params: { client_id: clientId } }).then((r) => setPosts(r.data));
    api.get("/files", { params: { client_id: clientId } }).then((r) => setFiles(r.data));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  useLiveReload(load);
  useEffect(() => { window.addEventListener("posts-changed", load); return () => window.removeEventListener("posts-changed", load); }, [load]);

  const move = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const unscheduled = posts.filter((p) => !p.scheduled_at);

  return (
    <div className="max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400 mb-3">{client?.name} · Kalender</p>
          <h1 className="text-4xl sm:text-7xl font-bold tracking-tight leading-none" data-testid="calendar-title">{MONTHS[cursor.getMonth()]} <span className="text-teal-400">{cursor.getFullYear()}</span></h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }} data-testid="calendar-today-button">Heute</Button>
          <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></Button>
          {isAdmin && <AutofillButton start={ymd(cursor)} label="Aus Drive einplanen" onDone={load} />}
          {isAdmin && <Button onClick={() => setEditor({})} className="bg-teal-500 hover:bg-teal-400 text-slate-950 gap-2" data-testid="new-post-button"><Plus className="h-4 w-4" />Neuer Post</Button>}
        </div>
      </div>

      <div className="hidden lg:block">
        <MonthGrid cursor={cursor} posts={posts} files={files} rhythm={client?.posting_rhythm || []} onPostClick={setDetail} onDayClick={isAdmin ? (key) => setEditor({ date: key }) : undefined} />
      </div>
      <div className="lg:hidden"><PostItAgenda cursor={cursor} posts={posts} files={files} onPostClick={setDetail} /></div>

      {isAdmin && unscheduled.length > 0 && (
        <div className="mt-8">
          <h2 className="text-base md:text-lg font-semibold mb-3">Ohne Termin</h2>
          <div className="flex flex-wrap gap-2">{unscheduled.map((p) => <button key={p.id} onClick={() => setDetail(p)} className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-white/5" data-testid={`unscheduled-post-${p.id}`}>{p.title || "Ohne Titel"}</button>)}</div>
        </div>
      )}

      {detail && <PostDetailDialog post={detail} onClose={() => setDetail(null)} onChanged={load} onEdit={isAdmin ? (p) => { setDetail(null); setEditor({ post: p }); } : undefined} />}
      {editor && <PostEditorDialog post={editor.post} defaultDate={editor.date} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
    </div>
  );
}
